import React, { useState, useEffect } from 'react';
import '../Sidebar/Sidebar.css';
import { FaBars, FaTimes, FaHome, FaWpforms, FaLink, FaSignOutAlt, FaTrophy, FaChartBar, FaShoppingCart, FaCoins, FaTicketAlt, FaUser } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/images/seedxpert-light.png';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

const Sidebar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false); // Başlangıçta kapalı
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Kullanıcı bilgilerini yükle
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem('userToken');
        if (token) {
          const response = await fetch('/api/user/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUserInfo(data.user); // response.user olarak geliyor
          }
        }
      } catch (error) {
        console.error('Kullanıcı bilgileri yüklenemedi:', error);
      }
    };

    fetchUserInfo();
  }, []);

  // Mobil kontrol için resize event listener
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // Desktop'a geçerken sidebar kapalı kalsın
      // Mobil'e geçerken zaten kapalı olacak
      if (mobile) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    // İlk yükleme kontrolü - her durumda kapalı başla
    setIsOpen(false);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    console.log('Toggle butonu tıklandı, mevcut durum:', isOpen);
    setIsOpen(prevState => {
      const newState = !prevState;
      console.log('Yeni durum:', newState);
      return newState;
    });
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Mobil görünümde menüyü kapat
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('userToken');
    navigate('/');
  };

  // Aktif sayfa kontrolü
  const isActivePage = (path) => {
    return location.pathname === path;
  };

  return (
    <>
    
      {/* Mobil için toggle butonu - her zaman görünür */}
      {isMobile && (
        <button 
          className="mobile-toggle-button" 
          type="button" 
          onClick={toggleSidebar}
        >
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      )}

      <div className={`sidebar-container ${isOpen ? 'open' : 'closed'}`}>
        {/* Header Bölümü */}
        <div className="sidebar-header">
          {/* Desktop toggle button - sadece desktop'ta görünür */}
          {!isMobile && (
            <button className="toggle-button" type="button" onClick={toggleSidebar}>
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          )}

          <div className="logo-container">
            <img src={logo} alt="SeedXpert" className="sidebar-logo" />
          </div>
        </div>

        {/* Kullanıcı Bilgileri */}
        <div className={`user-info-section ${userInfo ? 'loaded' : 'loading'}`}>
          {userInfo ? (
            <>
              <div className="user-avatar">
                <FaUser />
              </div>
              <div className="user-details">
                <div className="user-name">
                  {userInfo.firstName && userInfo.lastName
                    ? `${userInfo.firstName} ${userInfo.lastName}`
                    : userInfo.name || t('sidebar.user')}
                </div>
                <div className="user-email">{userInfo.email}</div>
                <div className="user-token">
                  <FaCoins className="token-icon" />
                  <span>
                    {userInfo.unlimitedPackageExpiresAt && new Date(userInfo.unlimitedPackageExpiresAt) > new Date()
                      ? t('sidebar.unlimited')
                      : `${userInfo.token || 0} ${t('sidebar.token')}`}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="user-skeleton">
              <div className="skeleton-avatar"></div>
              <div className="skeleton-details">
                <div className="skeleton-line skeleton-name"></div>
                <div className="skeleton-line skeleton-email"></div>
                <div className="skeleton-line skeleton-token"></div>
              </div>
            </div>
          )}
        </div>

        {/* İçerik Bölümü */}
        <div className="sidebar-content">
          <ul>
            <li>
              <a
                onClick={() => handleNavigation('/landing')}
                className={isActivePage('/landing') ? 'active' : ''}
              >
                <FaHome className="sidebar-icon" />
                <span className="sidebar-text">{t('sidebar.seedxpertScore')}</span>
              </a>
            </li>
          </ul>
          <ul>
            <li>
              <a
                onClick={() => handleNavigation('/dashboard')}
                className={isActivePage('/dashboard') ? 'active' : ''}
              >
                <FaChartBar className="sidebar-icon" />
                <span className="sidebar-text">{t('sidebar.reports')}</span>
              </a>
            </li>
          </ul>
          <ul>
            <li>
              <a
                onClick={() => handleNavigation('/shop')}
                className={isActivePage('/shop') ? 'active' : ''}
              >
                <FaShoppingCart className="sidebar-icon" />
                <span className="sidebar-text">{t('sidebar.shop')}</span>
              </a>
            </li>
          </ul>
          <ul>
            <li>
              <a
                onClick={() => handleNavigation('/profile')}
                className={isActivePage('/profile') ? 'active' : ''}
              >
                <FaUser className="sidebar-icon" />
                <span className="sidebar-text">{t('sidebar.profile')}</span>
              </a>
            </li>
          </ul>
        </div>

        {/* Footer Bölümü */}
        <div className="sidebar-language-switcher">
            <LanguageSwitcher />
          </div>
        <div className="sidebar-footer">
          <ul>
            <li>
              <a className='logout-link' onClick={handleLogout}>
                <FaSignOutAlt className="sidebar-icon" />
                <span className="sidebar-text">{t('common.logout')}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Mobil overlay - sidebar açıkken arka planı karart */}
      {isOpen && isMobile && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
    </>
  );
};

export default Sidebar;